import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { LeaveRequest, LeaveStatus } from '../../types/leave-manager.types';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';

const LEAVE_STATUSES: LeaveStatus[] = ['Pending', 'Approved', 'Rejected', 'Withdrawn', 'Cancelled'];

interface CreateLeaveRequestsTableColumnsProps {
  t: (key: string) => string;
  /** Adds the selection column for bulk approve/reject (holders of LEAVE.REVIEW); AdvanceDataTable
   * draws each row's own checkbox (disabled per its `canSelectRow`), this is only the header's
   * select-all, scoped to the rows that `canSelectRow` actually allows (`row.getCanSelect()`). */
  selectable?: boolean;
  /** The employee's email by IAM user id, shown under the name. */
  emailOf?: (userId: string) => string | undefined;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

export const createLeaveRequestsTableColumns = ({
  t,
  selectable = false,
  emailOf,
  departments = [],
}: CreateLeaveRequestsTableColumnsProps): ColumnDef<LeaveRequest>[] => [
  ...(selectable
    ? [
        {
          id: 'select',
          enableSorting: false,
          enableHiding: false,
          header: ({ table }) => {
            const selectableRows = table.getRowModel().rows.filter((row) => row.getCanSelect());
            const allSelected =
              selectableRows.length > 0 && selectableRows.every((row) => row.getIsSelected());
            return (
              <Checkbox
                checked={allSelected}
                disabled={selectableRows.length === 0}
                onCheckedChange={(value) =>
                  selectableRows.forEach((row) => row.toggleSelected(Boolean(value)))
                }
                aria-label={t('SELECT_ALL')}
                className="border-muted-foreground data-[state=checked]:border-none border-2"
              />
            );
          },
        } as ColumnDef<LeaveRequest>,
      ]
    : []),
  {
    // Card: the name alone as the title (the email has its own value below), the status top right.
    id: 'DisplayName',
    meta: { filter: { type: 'text' }, mobile: 'hidden' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName ?? '',
    cell: ({ row }) => (
      <UserNameCell
        name={row.original.DisplayName}
        email={row.original.Email || emailOf?.(row.original.UserId)}
      />
    ),
  },
  {
    id: 'CardEmployeeName',
    enableSorting: false,
    meta: { mobile: 'title', cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName || '-',
  },
  {
    // Card body, two per line: Email | Department, Leave Category | Start, End; the reason in the
    // footer.
    id: 'CardEmail',
    enableSorting: false,
    meta: { mobile: 'body', mobileOrder: 0, cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    // The request's own copy first: it needs no profile read, which a reviewer may not be allowed.
    accessorFn: (row) => row.Email || emailOf?.(row.UserId) || '-',
    cell: ({ getValue }) => <span className="break-all">{getValue() as string}</span>,
  },
  {
    // Sorted and filtered by the department name stored on the request (the column id is the field
    // both use); the filter is a dropdown of the departments the user may see, and the list's
    // department scope (by id) still applies on top.
    id: 'DepartmentName',
    meta: {
      mobile: 'body',
      mobileOrder: 1,
      filter: {
        type: 'options',
        dropdown: true,
        options: departments.map((department) => ({
          value: department.Title,
          label: department.Title,
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName || '-',
  },
  {
    id: 'Title',
    meta: { filter: { type: 'text' }, mobile: 'body', mobileOrder: 2 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LEAVE_CATEGORY')} />,
    accessorFn: (row) => row.Title,
  },
  {
    id: 'StartDate',
    meta: { filter: { type: 'date' }, mobile: 'body', mobileOrder: 3 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('START_DATE')} />,
    accessorFn: (row) => row.StartDate,
    cell: ({ row }) => formatDateOnly(row.original.StartDate),
  },
  {
    id: 'EndDate',
    meta: { filter: { type: 'date' }, mobile: 'body', mobileOrder: 4 },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('END_DATE')} />,
    accessorFn: (row) => row.EndDate,
    cell: ({ row }) => formatDateOnly(row.original.EndDate),
  },
  {
    id: 'Reason',
    // Card: in the footer, as in the old portal.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REASON')} />,
    accessorFn: (row) => row.Reason?.trim() || '-',
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: LEAVE_STATUSES.map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
];
